import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Grid, Spin, Table } from 'antd';
import * as echarts from 'echarts';
import { formatCurrency } from '@wavvest/shared-utils';
import { formatSecurityAmount } from './format';
import { ALLOCATION_CLASSES } from './allocation';

export { ALLOCATION_CLASSES };

export type AllocationValues = Record<string, number>;

type HoldingLike = {
  asset_id?: string | null;
  cost_basis_metadata?: unknown;
  symbol?: string | null;
  description?: string | null;
  amount?: string | null;
  stock_unit_price?: string | null;
  value_usd?: string | null;
};

type TransferLike = {
  deposit_id?: string;
  withdrawal_id?: string;
  amount?: string;
  created_at?: string;
  state?: string;
  [key: string]: unknown;
};

export type AccountDetailsPanelProps = {
  accountId: string;
  costBasisSummary?: React.ReactNode;
  renderCostBasis?: (holding: HoldingLike, gain: boolean) => React.ReactNode;
  totalEquity: number;
  /** `undefined` while holdings are still loading. */
  holdings?: (HoldingLike | null | undefined)[];
  /** The holdings request failed; show that instead of "No holdings yet". */
  holdingsError?: boolean;
  /** Let the holdings table grow with its rows instead of scrolling past ~200px. */
  uncappedHoldings?: boolean;
  transfers?: { deposits?: TransferLike[]; withdrawals?: TransferLike[] };
  /** `null` while the allocation is still being calculated. */
  allocation: AllocationValues | null;
  theme?: string;
  formatDate?: (value: string) => string;
};

const toNumber = (value: unknown): number => {
  const parsed = parseFloat(String(value ?? ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const isCash = (holding: HoldingLike) => holding.symbol?.toUpperCase() === 'USD';

const holdingPrice = (holding: HoldingLike) =>
  isCash(holding) ? 1 : toNumber(holding.stock_unit_price);

// Prefer the value Apex reports; derive it from shares × price only when missing.
// A zero-share position can still carry a value, and deriving it would show $0.
const holdingBalance = (holding: HoldingLike): number => {
  const reported = parseFloat(String(holding.value_usd ?? ''));
  return Number.isFinite(reported) ? reported : toNumber(holding.amount) * holdingPrice(holding);
};

const cssValue = (element: Element | null, variableName: string, fallback: string): string => {
  if (!element) return fallback;
  return window.getComputedStyle(element).getPropertyValue(variableName).trim() || fallback;
};

const cssNumber = (element: Element | null, variableName: string, fallback: number): number => {
  const value = parseFloat(cssValue(element, variableName, String(fallback)));
  return Number.isFinite(value) ? value : fallback;
};

const securityLabel = (holding: HoldingLike): string => {
  if (isCash(holding)) return 'Cash';
  const symbol = holding.symbol?.trim();
  const description = holding.description?.trim();
  if (symbol && description) return `${symbol} (${description})`;
  return symbol || description || 'Unknown security';
};

const AccountDetailsPanelContent: React.FC<AccountDetailsPanelProps> = ({
  accountId,
  costBasisSummary,
  renderCostBasis,
  totalEquity,
  holdings: rawHoldings,
  holdingsError = false,
  uncappedHoldings = false,
  transfers,
  allocation,
  theme = 'dark',
  formatDate = (value) => new Date(value).toLocaleDateString(),
}) => {
  const screens = Grid.useBreakpoint();
  // Apex can send empty entries; skip them rather than crash on field reads.
  const holdings = useMemo(
    () =>
      rawHoldings === undefined
        ? undefined
        : rawHoldings.filter((holding): holding is HoldingLike => Boolean(holding)),
    [rawHoldings],
  );
  const pieRef = useRef<HTMLDivElement>(null);
  const pieInstance = useRef<echarts.ECharts | null>(null);

  const pieData = useMemo(
    () =>
      ALLOCATION_CLASSES.map((item) => ({
        name: item.name,
        value: allocation?.[item.name] ?? 0,
        itemStyle: { color: item.color },
      })),
    [allocation],
  );

  const pieOption = useCallback(() => {
    const valueColor = cssValue(
      pieRef.current,
      '--wealth-prep-value-color',
      theme === 'dark' ? '#f2f4f7' : '#101827',
    );
    const valueFont = cssValue(
      pieRef.current,
      '--wealth-prep-value-font',
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    );
    const valueSize = cssNumber(pieRef.current, '--wealth-prep-value-size', 22);
    const valueWeight = cssValue(pieRef.current, '--wealth-prep-value-weight', '500');

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: { name: string; value: number; percent: number; color: string }) => `
            <div style="font-weight: bold; margin-bottom: 4px;">${params.name}</div>
            <div style="display: flex; align-items: center;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${params.color}; margin-right: 8px;"></span>
              <span>${formatCurrency(params.value)} (${params.percent}%)</span>
            </div>
          `,
      },
      legend: { show: false },
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: 'center',
          style: {
            text: formatCurrency(totalEquity),
            textAlign: 'center',
            fill: valueColor,
            fontFamily: valueFont,
            fontSize: valueSize,
            fontWeight: valueWeight,
          },
        },
      ],
      series: [
        {
          name: 'Asset Allocation',
          type: 'pie',
          radius: ['80%', '95%'],
          avoidLabelOverlap: false,
          label: { show: false, position: 'center' },
          emphasis: { label: { show: false } },
          labelLine: { show: false },
          data: pieData,
        },
      ],
    };
  }, [pieData, theme, totalEquity]);

  const ready = holdings !== undefined && allocation !== null;

  useEffect(() => {
    if (!ready || !pieRef.current) {
      return undefined;
    }
    pieInstance.current?.dispose();
    pieInstance.current = echarts.init(pieRef.current);
    pieInstance.current.setOption(pieOption());
    const onResize = () => pieInstance.current?.resize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      pieInstance.current?.dispose();
      pieInstance.current = null;
    };
  }, [ready, pieOption]);

  const allocationRows = useMemo(() => {
    const total = pieData.reduce((sum, item) => sum + item.value, 0);
    return pieData.map((item, index) => ({
      key: index.toString(),
      assetClass: item.name,
      weight: total > 0 ? `${((item.value / total) * 100).toFixed(1)}%` : '0%',
      value: formatCurrency(item.value),
      color: item.itemStyle.color,
    }));
  }, [pieData]);

  const holdingRows = useMemo(() => {
    if (!holdings) return [];
    const total = holdings.reduce((sum, h) => sum + toNumber(h.value_usd), 0);
    return holdings.map((holding, index) => {
      const value = toNumber(holding.value_usd);
      return {
        ...holding,
        weight: total > 0 ? (value / total) * 100 : 0,
        key: `holding-${accountId}-${holding.symbol}-${index}`,
      };
    });
  }, [holdings, accountId]);
  const cappedHoldings = !uncappedHoldings && holdingRows.length > 0;

  const transferRows = useMemo(
    () => [
      ...(transfers?.deposits ?? []).map((deposit, index) => ({
        ...deposit,
        key: `deposit-${accountId}-${deposit.deposit_id || index}`,
      })),
      ...(transfers?.withdrawals ?? []).map((withdrawal, index) => ({
        ...withdrawal,
        key: `withdrawal-${accountId}-${withdrawal.withdrawal_id || index}`,
      })),
    ],
    [transfers, accountId],
  );

  // Right-align figures once cost columns are present (matches the v2 design);
  // callers without cost basis keep their existing layout.
  const numericAlign = renderCostBasis ? ('right' as const) : undefined;
  const holdingColumns = [
    {
      title: 'Security',
      dataIndex: 'description',
      key: 'description',
      render: (_text: string, record: HoldingLike) => securityLabel(record),
    },
    {
      title: 'Shares',
      align: numericAlign,
      dataIndex: 'amount',
      key: 'amount',
      render: (value: string) => toNumber(value).toFixed(2),
    },
    {
      title: 'Last Price',
      align: numericAlign,
      dataIndex: 'stock_unit_price',
      key: 'stock_unit_price',
      render: (_value: unknown, record: HoldingLike) =>
        record == null ? '' : formatSecurityAmount(holdingPrice(record)),
    },
    {
      title: 'Account Balance',
      align: numericAlign,
      dataIndex: 'value',
      key: 'value',
      render: (_value: unknown, record: HoldingLike) =>
        formatSecurityAmount(holdingBalance(record)),
    },
    ...(renderCostBasis
      ? [
          {
            title: 'Cost basis',
            align: numericAlign,
            key: 'cost-basis',
            render: (_value: unknown, record: HoldingLike) => renderCostBasis(record, false),
          },
          {
            title: 'Gain / Loss',
            align: numericAlign,
            key: 'gain-loss',
            render: (_value: unknown, record: HoldingLike) => renderCostBasis(record, true),
          },
        ]
      : []),
    {
      title: 'Weight',
      align: numericAlign,
      dataIndex: 'weight',
      key: 'weight',
      render: (value: number) => `${value.toFixed(2)}%`,
    },
  ];

  const transferColumns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (_value: unknown, record: TransferLike) =>
        'deposit_id' in record ? 'Deposit' : 'Withdrawal',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: string) => formatCurrency(toNumber(amount)),
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Status',
      dataIndex: 'state',
      key: 'state',
      render: (state?: string) => state || '—',
    },
  ];

  const allocationColumns = [
    {
      title: 'Asset Class',
      dataIndex: 'assetClass',
      key: 'assetClass',
      render: (text: string, record: { color: string }) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span
            style={{
              height: '10px',
              width: '10px',
              backgroundColor: record.color,
              borderRadius: '50%',
              display: 'inline-block',
              marginRight: '12px',
            }}
          />
          <span>{text}</span>
        </div>
      ),
    },
    { title: 'Weight', dataIndex: 'weight', key: 'weight' },
    { title: 'Value', dataIndex: 'value', key: 'value' },
  ];

  if (!ready) {
    return (
      <div
        className="expanded-content-container"
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}
      >
        <Spin size="large" tip="Loading allocation data..." />
      </div>
    );
  }

  return (
    <div className={`expanded-content-container${renderCostBasis ? ' cost-basis-enabled' : ''}`}>
      <div className="account-asset-allocation-container">
        <div className="account-asset-allocation">
          <div className="allocation-table-container">
            <Table
              columns={allocationColumns}
              dataSource={allocationRows}
              pagination={false}
              size="small"
              rowKey="key"
            />
          </div>
          <div className="allocation-chart-container">
            <div className="market-value-label">Market value</div>
            <div ref={pieRef} className="pie-chart" style={{ height: '168px', width: '168px' }} />
          </div>
        </div>
      </div>

      {costBasisSummary}
      <div className="account-details-bottom">
        <div className="holdings-section">
          <div className="section-title-bar">Holdings</div>
          {renderCostBasis && !screens.md ? (
            <div className="cost-basis-mobile-holdings">
              {holdingRows.length === 0 ? (
                <p>{holdingsError ? "We couldn't load holdings." : 'No holdings yet.'}</p>
              ) : (
                holdingRows.map((holding) => (
                  <div className="cost-basis-mobile-holding" key={holding.key}>
                    <div>
                      <strong>{securityLabel(holding)}</strong>
                      <span>{formatSecurityAmount(holdingBalance(holding))}</span>
                    </div>
                    <div>
                      <span>{toNumber(holding.amount).toFixed(2)} shares</span>
                      {renderCostBasis(holding, true)}
                    </div>
                    <div>
                      <span>Cost basis</span>
                      {renderCostBasis(holding, false)}
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <Table
              columns={holdingColumns}
              dataSource={holdingRows}
              pagination={false}
              size="small"
              locale={{
                emptyText: holdingsError
                  ? "We couldn't load holdings for this account. Try again later."
                  : 'No holdings yet.',
              }}
              scroll={
                renderCostBasis
                  ? { x: 850, ...(cappedHoldings ? { y: 200 } : {}) }
                  : cappedHoldings
                    ? { y: 200 }
                    : undefined
              }
              rowKey="key"
            />
          )}
        </div>
        <div className="transfers-section">
          <div className="section-title-bar">Recent Transfers</div>
          <Table
            columns={transferColumns}
            dataSource={transferRows}
            pagination={false}
            size="small"
            locale={{ emptyText: 'No transfers yet.' }}
            rowKey="key"
          />
        </div>
      </div>
    </div>
  );
};

// A bad record must not take down the whole wealth page; contain it to this panel.
class AccountDetailsErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    console.error('Failed to render account details', error);
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="expanded-content-container" role="alert">
          We couldn't show this account's details. Try again later.
        </div>
      );
    }
    return this.props.children;
  }
}

const AccountDetailsPanel: React.FC<AccountDetailsPanelProps> = (props) => (
  <AccountDetailsErrorBoundary key={props.accountId}>
    <AccountDetailsPanelContent {...props} />
  </AccountDetailsErrorBoundary>
);

export default AccountDetailsPanel;
